import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { usePersistentValueLocal } from '../hooks/usePersistentState';
import Card from '../components/Card';
import { getBsView } from '../api/financials_bs';
import { getSocialChargesPlan } from '../api/socialChargesPlan';
import SocialPhasingTab from './treasury/tabs/SocialPhasingTab';
import './ForecastSocial.css';

/* Onglet « Charges personnel & sociales » (Prévisionnel) — vue PONT en 3 blocs,
   selon docs/previsionnel-integre.md (§ charges sociales) :
     1. Accrual   — charge économique (People) : ce qui va au compte de résultat.
     2. Décaissement — cash réel, phasé par assurance (échéancier payment_months).
     3. Solde de dette — accumulateur bilan (accrual − décaissement, cumulé).
   Identité de contrôle par charge : solde_fin = solde_début + accrual − décaissement.
   Moteur : le plan serveur (source unique, partagée avec People). Le
   phasing s'édite dans une pop-up (bouton), pas inline. Année N (12 mois). */

const fmt = (v, unit) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Math.round(Number(v) / (unit === 'chf' ? 1 : 1000));
  return n === 0 ? '–' : n.toLocaleString('de-CH');
};
const sum12 = (arr) => arr.slice(0, 12).reduce((a, b) => a + (Number(b) || 0), 0);

/* `plan` (2026-09-30) : un plan déjà calculé, celui des Salaires prévisionnels,
   lu à la place de celui de People ; `hideSalaries` retire le brut et le net,
   que la table du dessus porte déjà. Même vue, même grammaire. */
export default function ForecastSocialTab({ activeTenant, year, unit = 'kchf', closeAction = null, plan: planProp = null, hideSalaries = false, title = null }) {
  const { t } = useTranslation();
  const p = useP();
  const monthLabels = useMemo(
    () => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)),
    [t],
  );
  const tid = activeTenant?.id;
  const N = year;
  const [plan, setPlan] = useState(null);   // plan CALCULÉ PAR LE SERVEUR
  const [showPhasing, setShowPhasing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  /* Groupes repliables (charges calculées · décaissements · solde théorique ·
     cotisations de base) — état MÉMORISÉ : on ne redéplie pas tout à chaque
     visite (retour Olivier 2026-08-17). */
  const [fold, setFold] = usePersistentValueLocal('fsoc_fold', { accrual: true, cash: true, debt: true, base: true, ins: true, lpp: true });
  // Bilan : mois clôturés (démarcation réel/planifié) + solde d'ouverture recordé
  // de la ligne « Assurances sociales » (social_debt) = poste ouvert de la dette.
  // socialMonths = solde comptabilisé « Assurances sociales » au bilan, par mois
  // (réel sur les mois clos) → ligne de référence connectée au bilan.
  const [bsInfo, setBsInfo] = useState({ closed: 0, socialOpening: 0, socialMonths: Array(12).fill(0) });
  useEffect(() => {
    if (!tid) { setBsInfo({ closed: 0, socialOpening: 0, socialMonths: Array(12).fill(0) }); return; }
    getBsView(tid, N, { flatForecast: true }).then((r) => {
      if (r.error) return;
      const lid = (r.data?.lines || []).find((l) => l.std_key === 'social_debt')?.id;
      const sr = (r.data?.rows || []).find((x) => x.code === lid);
      const months = Array.from({ length: 12 }, (_, i) => Number(sr?.months?.[String(i + 1)]) || 0);
      setBsInfo({ closed: r.data?.closed_count || 0, socialOpening: Number(sr?.prev_dec) || 0, socialMonths: months });
    }).catch(() => {});
  }, [tid, N]);
  const closed = bsInfo.closed;

  /* Le plan est CALCULÉ PAR LE SERVEUR (moteur migré du JavaScript le
     2026-08-21) : cette vue LIT — plus de calcul local, plus de série publiée
     (le bilan consomme la même source en live, quel que soit l'écran ouvert). */
  const load = useCallback(() => {
    if (!tid || planProp) return;
    getSocialChargesPlan(tid, N)
      .then((r) => setPlan(r.error ? null : r.data))
      .catch(() => setPlan(null));
  }, [tid, N, planProp]);
  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => { if (planProp) setPlan(planProp); }, [planProp]);

  // Décaissement par charge (échéancier) indexé par libellé.
  const invByLbl = useMemo(() => {
    const m = {};
    (plan?.invoices || []).forEach((iv) => { m[iv.libelle] = iv.inv; });
    return m;
  }, [plan]);

  if (!tid) return <p className="fps-loading">{p("Sélectionne d'abord une entreprise.")}</p>;
  if (!plan) return <p className="fps-loading">{p('Chargement…')}</p>;
  if (!plan.hasData) return <p className="fps-loading">{p('Aucune charge sociale saisie pour')} {N} {p('(People → Charges sociales).')}</p>;

  const activeCharges = (plan.charges || []).filter((c) => c.hasEmpl || c.hasEmpr);

  // Lignes d'affichage : la base FUSIONNÉE en une seule ligne « Cotisations de base »
  // (calcul + décaissement), puis chaque assurance à part. Chaque ligne porte
  // accrual (salaires réels × taux), cash (décaissement) et dette (cumul du gap).
  const mkRow = (libelle, section, list) => {
    const accrual = Array(12).fill(0);
    const cash = Array(12).fill(0);
    list.forEach((c) => {
      const inv = invByLbl[c.libelle] || [];
      for (let i = 0; i < 12; i += 1) { accrual[i] += c.empl[i] + c.empr[i]; cash[i] += Number(inv[i]) || 0; }
    });
    const debt = Array(12).fill(0); let bal = 0;
    for (let i = 0; i < 12; i += 1) { bal += accrual[i] - cash[i]; debt[i] = bal; }
    return { libelle, section, accrual, cash, debt };
  };
  /* TROIS GROUPES (demande d'Olivier, 2026-09-30) : les cotisations de base,
     les assurances, la LPP, chacun repliable sur son détail. Une assurance par
     ligne allongeait la table de dix rangs pour une lecture d'ensemble. */
  const isLpp = (c) => /lpp/i.test(c.libelle || '');
  const baseCharges = activeCharges.filter((c) => c.section === 'base');
  const insurCharges = activeCharges.filter((c) => c.section === 'assurance' && !isLpp(c));
  const lppCharges = activeCharges.filter((c) => c.section === 'assurance' && isLpp(c));
  const GROUPS = [
    { key: 'base', label: p('Cotisations de base'), list: baseCharges },
    { key: 'ins', label: p('Assurances'), list: insurCharges },
    { key: 'lpp', label: p('LPP'), list: lppCharges },
  ].filter((g) => g.list.length);
  const displayRows = GROUPS.map((g) => ({ ...mkRow(g.label, g.key, g.list), group: g }));

  // % de la ligne (total annuel) rapporté aux salaires bruts annuels.
  const grossAnnual = sum12(plan.gross);
  const pctFmt = (vals) => (grossAnnual ? `${Math.round((sum12(vals) / grossAnnual) * 1000) / 10}%` : '–');

  // Ligne de tableau générique (12 mois + total + % du brut). noPct : masque le
  // % (ex. solde théorique = un solde, un pourcentage du brut n'a pas de sens).
  const row = (label, vals, cls = '', key, noPct = false) => (
    <tr key={key} className={cls}>
      <td className="fsoc-lbl"><span className="fsoc-gut" />{label}</td>
      {monthLabels.map((_, i) => {
        const v = vals[i];
        return <td key={i} className={`fsoc-num${Number(v) < 0 ? ' is-neg' : ''}${i === closed ? ' fsoc-fc-start' : ''}`}>{fmt(v, unit)}</td>;
      })}
      <td className="fsoc-num fsoc-total">{fmt(sum12(vals), unit)}</td>
      <td className="fsoc-num fsoc-pct">{noPct ? '' : pctFmt(vals)}</td>
    </tr>
  );

  // Détail d'un bloc : charges groupées par section (base / assurances) avec un
  // libellé de section léger. getVals(c) → tableau 12 mois pour la charge c.
  // Plus d'en-tête de section : la base est une ligne unique, les assurances
  // suivent directement — les libellés se suffisent.
  /* « Cotisations de base » regroupe AVS/AI/APG, AC, allocations, formation,
     frais administratifs : deuxième niveau de dégroupage, même grammaire
     (chevron au survol, gouttière fixe) — demande Olivier 2026-08-17. */
  const blockDetails = (getVals, cls = '') =>
    displayRows.flatMap((r, idx) => {
      const base = `fsoc-detail-row ${cls}`.trim();
      const gk = r.group.key;
      const details = r.group.list.map((c) => mkRow(c.libelle, gk, [c]));
      if (details.length < 2) {
        return [row(details.length ? details[0].libelle : r.libelle, getVals(r), base, `r-${cls}-${idx}`)];
      }
      const head = (
        <tr key={`r-${cls}-${idx}`} className={`${base} fsoc-sub`}
          onClick={() => setFold((f) => ({ ...f, [gk]: !f[gk] }))}>
          <td className="fsoc-lbl">
            <span className={`fsoc-chev${fold[gk] ? '' : ' is-open'}`} aria-hidden>›</span>
            {r.libelle}
          </td>
          {monthLabels.map((_, i) => {
            const v = getVals(r)[i];
            return <td key={i} className={`fsoc-num${Number(v) < 0 ? ' is-neg' : ''}${i === closed ? ' fsoc-fc-start' : ''}`}>{fmt(v, unit)}</td>;
          })}
          <td className="fsoc-num fsoc-total">{fmt(sum12(getVals(r)), unit)}</td>
          <td className="fsoc-num fsoc-pct">{pctFmt(getVals(r))}</td>
        </tr>
      );
      if (fold[gk]) return [head];
      return [head, ...details.map((b, j) =>
        row(b.libelle, getVals(b), `${base} fsoc-detail2`, `r-${cls}-${idx}-${j}`))];
    });

  // Total mensuel du bloc « solde de dette » (somme des lignes d'affichage).
  // Solde de dette = OUVERTURE (31.12 N-1) + cumul du mouvement (accrual −
  // décaissement). L'ouverture ancre le solde sur le réel du bilan → le total
  // égale la ligne « Assurances sociales » du Bilan prévisionnel.
  const socialOpening = bsInfo.socialOpening || 0;
  const openingRow = Array(12).fill(socialOpening);
  const debtTotal = Array.from({ length: 12 }, (_, i) =>
    socialOpening + displayRows.reduce((s, r) => s + (r.debt[i] || 0), 0));

  // Décaissement = flux du plan (base + assurances).
  const cashTotal = plan.totFact;
  const bilanSocial = bsInfo.socialMonths || Array(12).fill(0);

  // En-tête de colonnes (mois) — répété dans chaque card pour aligner les colonnes.
  /* En-tête standard (modèle Plan services, cf. CLAUDE.md) : un bandeau RÉALISÉ
     en ardoise claire sur les mois clos, PRÉVISIONNEL en ardoise pleine sur les
     mois ouverts, puis la ligne des mois. */
  const nOpen = 12 - closed;
  const monthHead = (
    <thead>
      <tr className="fsoc-bandrow">
        <th className="fsoc-lbl" />
        {closed > 0 && <th className="fsoc-band fsoc-band--real" colSpan={closed}>{p('Réalisé')}</th>}
        {nOpen > 0 && <th className="fsoc-band fsoc-band--fore" colSpan={nOpen}>{p('Prévisionnel')}</th>}
        <th className="fsoc-num fsoc-total" />
        <th className="fsoc-num fsoc-pct" />
      </tr>
      <tr>
        <th className="fsoc-lbl" />
        {monthLabels.map((lab, i) => <th key={lab} className={`fsoc-num${i === closed ? ' fsoc-fc-start' : ''}`}>{lab}</th>)}
        <th className="fsoc-num fsoc-total">{p('Total')}</th>
        <th className="fsoc-num fsoc-pct" title={p('Part des salaires bruts annuels')}>{p('% vs Brut')}</th>
      </tr>
    </thead>
  );
  const tableCard = (children, extraCls = '') => (
    <div className="fsoc-wrap"><table className={`fsoc ${extraCls}`.trim()}>{monthHead}<tbody>{children}</tbody></table></div>
  );

  // Ligne de total d'un bloc, sous les lignes sommées. Le cumulé (running) n'a pas
  // de total ni de % pertinents (déjà cumulé colonne par colonne) → colonnes vides.
  const blockTotalRow = (title, totals, cls = '') => {
    const isCumul = cls.includes('fsoc-cumul');
    return (
      <tr className={`fsoc-blocktotal ${cls}`.trim()}>
        <td className="fsoc-lbl">{title}</td>
        {monthLabels.map((_, i) => <td key={i} className={`fsoc-num fsoc-block-tot${i === closed ? ' fsoc-fc-start' : ''}`}>{fmt(totals[i], unit)}</td>)}
        <td className="fsoc-num fsoc-total">{isCumul ? '' : fmt(sum12(totals), unit)}</td>
        <td className="fsoc-num fsoc-pct">{isCumul ? '' : pctFmt(totals)}</td>
      </tr>
    );
  };

  // Cumul mensuel (running total) d'une série sur 12 mois.
  const cumulative = (arr) => {
    const out = []; let s = 0;
    for (let i = 0; i < 12; i += 1) { s += Number(arr[i]) || 0; out.push(s); }
    return out;
  };

  // Contrôle : dette fin = ouverture + Σ accrual − Σ décaissement.
  const totAccrual = activeCharges.reduce((s, c) => s + sum12(c.empl) + sum12(c.empr), 0);
  const totCash = (plan.invoices || []).reduce((s, iv) => s + sum12(iv.inv), 0);
  const totDebtEnd = debtTotal[11];

  const uLabel = unit === 'chf' ? 'CHF' : 'kCHF';

  /* UNE SEULE card (décision Olivier 2026-08-17) : le premier tableau dit tout.
     Les trois lignes de synthèse — charges calculées, décaissements, solde
     théorique — sont des GROUPES repliables (chevron révélé au survol) qui
     ouvrent le détail par charge. Le mécanisme de masse salariale annoncée est
     retiré : décaissements et solde sont théoriques, dérivés de la paie. */
  const grpRow = (label, vals, key, noPct = false) => (
    <tr key={key} className="fsoc-grp" onClick={() => setFold((f) => ({ ...f, [key]: !f[key] }))}>
      <td className="fsoc-lbl">
        <span className={`fsoc-chev${fold[key] ? '' : ' is-open'}`} aria-hidden>›</span>
        {label}
      </td>
      {monthLabels.map((_, i) => (
        <td key={i} className={`fsoc-num${Number(vals[i]) < 0 ? ' is-neg' : ''}${i === closed ? ' fsoc-fc-start' : ''}`}>{fmt(vals[i], unit)}</td>
      ))}
      <td className="fsoc-num fsoc-total">{fmt(sum12(vals), unit)}</td>
      <td className="fsoc-num fsoc-pct">{noPct ? '' : pctFmt(vals)}</td>
    </tr>
  );

  return (
    <div className="fsoc-cards">
      <Card
        className="report-card"
        title={title || p('Charges sociales')}
        subtitle={`${N} · ${uLabel}`}
        actions={<>{<button type="button" className="fsoc-btn fsoc-btn--primary" onClick={() => setShowPhasing(true)}>{p('Échéancier de paiement')}</button>}{closeAction}</>}
      >
        {tableCard(<>
          {!hideSalaries && row(p('Salaires bruts'), plan.gross, 'fsoc-strong')}
          {!hideSalaries && row(p('Salaires nets versés'), plan.net, 'fsoc-strong')}

          {grpRow(p('Charges calculées'), plan.sub, 'accrual')}
          {!fold.accrual && blockDetails((r) => r.accrual)}

          {grpRow(p('Décaissements théoriques'), cashTotal, 'cash')}
          {!fold.cash && blockDetails((r) => r.cash)}

          {grpRow(p('Solde théorique'), debtTotal, 'debt', true)}
          {!fold.debt && (
            <>
              {row(p('Ouverture (31.12)'), openingRow, 'fsoc-detail-row', 'debt-open')}
              {blockDetails((r) => r.debt, 'fsoc-debt')}
              {row(p('Bilan, Assurances sociales (comptabilisé)'), bilanSocial, 'fsoc-detail-row fsoc-bilan-ref', 'debt-bilan')}
            </>
          )}
        </>)}
      </Card>

      {showPhasing && (
        <>
          <div className="fsoc-modal__scrim" onClick={() => setShowPhasing(false)} />
          <div className="fsoc-modal" role="dialog" aria-label={t('phrases:p_54346c0843')}>
            <div className="fsoc-modal__head">
              <span className="fsoc-modal__title">{p('Échéancier de paiement')} — {N}</span>
              <button type="button" className="fsoc-modal__x" onClick={() => setShowPhasing(false)} aria-label={p('Fermer')}>×</button>
            </div>
            <div className="fsoc-modal__body">
              <SocialPhasingTab
                tenantId={tid}
                year={N}
                addToast={() => {}}
                locked={false}
                onSaved={() => setReloadKey((k) => k + 1)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
